import { useEffect, useState } from 'react';

/**
 * Confirmacion para acciones que no se pueden deshacer.
 *
 * Cuando el borrado arrastra otras cosas (un proyecto se lleva sus tareas y su
 * chat) se pide escribir el nombre: obliga a leer que se esta borrando en vez
 * de aceptar por reflejo.
 */
export function ConfirmDialog({
  title,
  description,
  confirmLabel = 'Eliminar',
  requireTyping,
  pending = false,
  error,
  onConfirm,
  onCancel,
}: {
  title: string;
  description: React.ReactNode;
  confirmLabel?: string;
  /** Texto que hay que escribir para habilitar el boton. */
  requireTyping?: string;
  pending?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState('');
  const ready = !requireTyping || typed.trim() === requireTyping;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      role="presentation"
      onClick={onCancel}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-[rgba(4,4,10,.7)] p-6"
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[420px] overflow-hidden rounded-panel border border-line bg-surface shadow-[0_24px_60px_rgba(0,0,0,.6)]"
      >
        <div className="px-5 pb-4 pt-5">
          <h2 id="confirm-title" className="font-display text-base font-semibold">
            {title}
          </h2>
          <div className="mt-2 text-[13.5px] leading-relaxed text-subtle text-pretty">
            {description}
          </div>

          {requireTyping && (
            <label className="mt-4 block">
              <span className="mb-1.5 block text-[12.5px] font-semibold text-ink">
                Escribe <span className="font-mono text-danger">{requireTyping}</span> para
                confirmar
              </span>
              <input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoFocus
                className="w-full rounded-control border border-line-strong px-3 py-2 font-mono text-[13.5px] outline-none focus:border-danger"
              />
            </label>
          )}

          {error && (
            <p role="alert" className="mt-3 text-[13px] text-danger">
              {error}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-line-soft bg-sunken px-5 py-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-control px-3 py-2 text-[13px] font-medium text-subtle transition-colors hover:text-ink"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!ready || pending}
            className="rounded-control bg-danger px-4 py-2 text-[13px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
          >
            {pending ? 'Eliminando…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
